/**
 * Accepting an invitation.
 *
 * Reached by opening the link, which puts the token in the query string. The token is sent
 * once and never stored client-side: the API compares its hash and grants the membership,
 * and the URL is cleaned up immediately afterwards so a shared screenshot or a browser
 * history entry does not carry a live credential.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, ErrorState, Field, Input, Panel, Spinner, Wordmark } from "@onecpa/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";

export function AcceptInvitation({ token }: { token: string }) {
  const { user, signOutNow } = useAuth();
  const [fullName, setFullName] = useState(user?.displayName ?? "");
  const [done, setDone] = useState(false);

  const accept = useMutation({
    mutationFn: () =>
      api.acceptInvitation({ token, ...(fullName.trim() ? { fullName: fullName.trim() } : {}) }),
    onSuccess: () => setDone(true),
  });

  // Strip the token from the address bar as soon as the page has read it.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("invite")) {
      url.searchParams.delete("invite");
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  if (done) {
    // A full reload rather than client-side routing: the membership list is fetched once at
    // startup, and reloading is the honest way to pick up an org that did not exist a
    // moment ago.
    window.location.replace("/");
    return (
      <div className="grid min-h-dvh place-items-center bg-paper">
        <Spinner className="size-5 text-ledger" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-paper">
      <header className="flex items-center bg-night px-4 py-3">
        <Wordmark />
      </header>

      <main className="mx-auto max-w-md px-4 py-10">
        <h1 className="serif text-xl font-bold text-ink">הצטרפות למשרד</h1>
        <p className="mt-2 text-sm text-ink-muted">
          ההזמנה נבדקת מול הכתובת שאליה נשלחה. אם נכנסתם עם חשבון אחר, יש להתנתק ולהיכנס עם הכתובת
          שקיבלה את ההזמנה.
        </p>
        <p className="mt-1 text-xs text-ink-faint">
          מחוברים כרגע כ־<bdi dir="ltr">{user?.email}</bdi>
        </p>

        <Panel className="mt-6">
          <div className="flex flex-col gap-3.5 px-4 py-4">
            <Field label="שם מלא" help="כך תופיעו ברשימת העובדים ובשיוך משימות">
              {({ id }) => (
                <Input
                  id={id}
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                />
              )}
            </Field>

            <Button variant="primary" loading={accept.isPending} onClick={() => accept.mutate()}>
              הצטרפות
            </Button>

            <Button variant="ghost" onClick={() => void signOutNow()}>
              התנתקות והחלפת חשבון
            </Button>

            {accept.error && (
              <ErrorState
                title="לא ניתן להצטרף"
                message={(accept.error as Error).message}
                className="py-4"
              />
            )}
          </div>
        </Panel>
      </main>
    </div>
  );
}
